/**
 * Product Grid Loading State Manager
 *
 * Single source of truth for .product-grid--loading class lifecycle
 * Coordinates skeleton display with product item initialization
 *
 * Usage:
 *   ProductGridLoading.startLoading(gridElement)  - Show skeletons
 *   ProductGridLoading.finishLoading(gridElement) - Hide skeletons, init products
 */
class ProductGridLoadingManager {
    constructor() {
        // Track loading state per grid (by element reference)
        this.gridStates = new WeakMap();

        // Performance timing data
        this.timings = new Map();

        // Dev mode detection
        this.isDevMode = window.Shopify?.designMode ||
                         window.location.hostname === 'localhost' ||
                         window.location.search.includes('preview_theme_id');

        // MutationObserver instances per grid
        this.observers = new WeakMap();
    }

    /**
     * Start loading state - add loading class and show skeletons
     * @param {HTMLElement} gridElement - The product grid element
     * @param {string} [context='unknown'] - Context for logging (e.g., 'initial', 'filter')
     */
    startLoading(gridElement, context = 'unknown') {
        if (!gridElement) {
            console.warn('ProductGridLoading.startLoading: No grid element provided');
            return;
        }

        // Start timing
        const timingKey = `loading-${context}-${Date.now()}`;
        console.time(timingKey);
        this.log(`🔄 START Loading (${context})`, gridElement);

        // For filter context, replace existing products with skeletons immediately
        if (context === 'filter') {
            const existingProducts = gridElement.querySelectorAll('.product-item, [data-initialized="true"]');
            const skeletonCount = existingProducts.length || 12;

            this.log(`🗑️  Removing ${existingProducts.length} existing products`, gridElement);
            this.log(`💀 Injecting ${skeletonCount} skeletons`, gridElement);

            // Clear grid
            gridElement.innerHTML = '';

            // Inject skeleton placeholders
            const skeletonHTML = `
                <div class="relative flex flex-col h-full p-3 bg-white border rounded-md border-black-200 md:p-4 animate-pulse">
                    <div class="mb-3 overflow-hidden rounded-md md:mb-4 bg-black-200 aspect-square"></div>
                    <div class="flex flex-col gap-y-3 md:gap-y-4">
                        <div class="w-3/4 h-5 rounded bg-black-200"></div>
                        <div class="w-1/2 h-5 rounded bg-black-200"></div>
                    </div>
                </div>
            `;

            for (let i = 0; i < skeletonCount; i++) {
                const skeleton = document.createElement('div');
                skeleton.innerHTML = skeletonHTML.trim();
                gridElement.appendChild(skeleton.firstElementChild);
            }
        }

        // Add loading class
        gridElement.classList.add('product-grid--loading');

        // Store state
        this.gridStates.set(gridElement, {
            isLoading: true,
            context: context,
            timingKey: timingKey,
            startTime: performance.now()
        });
    }

    /**
     * Finish loading state - remove loading class and initialize products
     * @param {HTMLElement} gridElement - The product grid element
     */
    finishLoading(gridElement) {
        if (!gridElement) {
            console.warn('ProductGridLoading.finishLoading: No grid element provided');
            return;
        }

        const state = this.gridStates.get(gridElement);

        // Handle grids that were server-rendered with loading class but never had startLoading() called
        // This happens when the grid is replaced via DOM swap (e.g., filtering)
        if (!state) {
            if (gridElement.classList.contains('product-grid--loading')) {
                this.log('✅ FINISH Loading (inherited from server)', gridElement);
                gridElement.classList.remove('product-grid--loading');
                this.initializeProductItems(gridElement);
                return;
            } else {
                // Grid doesn't have loading class and no state - nothing to do
                this.log('ℹ️  No loading state to finish', gridElement);
                return;
            }
        }

        // Only proceed if currently loading
        if (!state.isLoading) {
            return;
        }

        this.log(`✅ FINISH Loading (${state.context})`, gridElement);

        // Remove loading class
        gridElement.classList.remove('product-grid--loading');

        // Initialize product items
        this.initializeProductItems(gridElement);

        // End timing
        if (state.timingKey) {
            console.timeEnd(state.timingKey);
        }

        const duration = performance.now() - state.startTime;
        this.log(`⏱️  Duration: ${duration.toFixed(2)}ms`, gridElement);

        // Update state
        this.gridStates.set(gridElement, {
            ...state,
            isLoading: false,
            endTime: performance.now()
        });
    }

    /**
     * Initialize all product items in the grid
     * Idempotent - safe to call multiple times
     * @param {HTMLElement} gridElement - The product grid element
     */
    initializeProductItems(gridElement) {
        if (!gridElement) return;

        // Find all uninitialized product items
        const items = gridElement.querySelectorAll('.js-product-item:not([data-initialized])');

        this.log(`🎯 Initializing ${items.length} product items`, gridElement);
        this.log(`Grid classes: ${gridElement.className}`, gridElement);

        if (items.length === 0) {
            this.log('ℹ️  No items to initialize', gridElement);
            return;
        }

        // Call global product item renderer if available
        if (window.productItemRenderer && typeof window.productItemRenderer.refresh === 'function') {
            window.productItemRenderer.refresh();
        } else {
            console.warn('ProductGridLoading: productItemRenderer not available');
        }
    }

    /**
     * Check if a grid is currently loading
     * @param {HTMLElement} gridElement - The product grid element
     * @returns {boolean}
     */
    isLoading(gridElement) {
        const state = this.gridStates.get(gridElement);
        return state?.isLoading || false;
    }

    /**
     * Logging helper with conditional output
     * @param {string} message - Log message
     * @param {HTMLElement} [element] - Optional element for context
     */
    log(message, element = null) {
        // Always log in dev mode, otherwise only errors/warnings
        if (this.isDevMode || message.includes('❌') || message.includes('⚠️')) {
            const prefix = '[ProductGridLoading]';
            if (element) {
                console.log(prefix, message, element);
            } else {
                console.log(prefix, message);
            }
        }
    }

    /**
     * Enable MutationObserver for a grid to auto-initialize new product items
     *
     * WHEN TO USE:
     * - Use for dynamic content that loads incrementally (infinite scroll, AJAX pagination)
     * - Use when you don't control the timing of item insertion
     *
     * WHEN NOT TO USE:
     * - DON'T use for filter-based replacements (we have explicit hooks for that)
     * - DON'T use if you can call finishLoading() explicitly after DOM changes
     *
     * The observer watches for .js-product-item elements being added and initializes them
     *
     * @param {HTMLElement} gridElement - The product grid element to observe
     */
    enableAutoInitObserver(gridElement) {
        if (!gridElement) {
            console.warn('ProductGridLoading.enableAutoInitObserver: No grid element provided');
            return;
        }

        // Don't create duplicate observers
        if (this.observers.has(gridElement)) {
            this.log('ℹ️  Observer already enabled for grid', gridElement);
            return;
        }

        this.log('👁️  Enabling MutationObserver for auto-initialization', gridElement);

        const observer = new MutationObserver((mutations) => {
            // Check if any .js-product-item elements were added
            let hasNewItems = false;

            for (const mutation of mutations) {
                if (mutation.type === 'childList') {
                    for (const node of mutation.addedNodes) {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            // Check if node itself is a product item
                            if (node.classList?.contains('js-product-item')) {
                                hasNewItems = true;
                                break;
                            }
                            // Or if it contains product items
                            if (node.querySelector?.('.js-product-item')) {
                                hasNewItems = true;
                                break;
                            }
                        }
                    }
                }
                if (hasNewItems) break;
            }

            if (hasNewItems) {
                this.log('🔍 Observer detected new product items, initializing...', gridElement);
                this.initializeProductItems(gridElement);
            }
        });

        // Observe child list changes
        observer.observe(gridElement, {
            childList: true,
            subtree: true
        });

        // Store observer reference
        this.observers.set(gridElement, observer);
    }

    /**
     * Disable MutationObserver for a grid
     * @param {HTMLElement} gridElement - The product grid element
     */
    disableAutoInitObserver(gridElement) {
        const observer = this.observers.get(gridElement);
        if (observer) {
            observer.disconnect();
            this.observers.delete(gridElement);
            this.log('👁️  Disabled MutationObserver', gridElement);
        }
    }
}

// Create singleton instance
if (typeof window !== 'undefined') {
    window.ProductGridLoading = window.ProductGridLoading || new ProductGridLoadingManager();
}